import React, { useState } from 'react';

const LOAN_TYPES = ['Personal Loan', 'Home Loan', 'Auto Loan', 'Business Loan'];
const EMPLOYMENT = ['Salaried', 'Self-Employed', 'Freelancer', 'Unemployed'];

export default function ConditionalWorkflow() {
  const [form, setForm] = useState({
    loanType: 'Personal Loan', amount: '', tenure: '', employment: 'Salaried',
    monthlyIncome: '', creditScore: '', collateral: false, collateralValue: '',
    businessYears: '', coApplicant: false, coApplicantIncome: '',
  });
  const [result, setResult] = useState(null);

  const ch = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    setResult(null);
  };

  const analyze = () => {
    const amt = parseFloat(form.amount) || 0;
    const income = parseFloat(form.monthlyIncome) || 0;
    const credit = parseInt(form.creditScore) || 0;
    const tenure = parseInt(form.tenure) || 0;
    const coIncome = form.coApplicant ? (parseFloat(form.coApplicantIncome) || 0) : 0;
    const totalIncome = income + coIncome;
    const bizYears = parseInt(form.businessYears) || 0;
    const collateralVal = form.collateral ? (parseFloat(form.collateralValue) || 0) : 0;

    let score = 0;
    const flags = [];
    const risks = [];

    if (credit >= 750) { score += 30; flags.push({ text: 'Excellent credit score', type: 'good' }); }
    else if (credit >= 650) { score += 20; flags.push({ text: 'Good credit score', type: 'ok' }); }
    else if (credit >= 550) { score += 5; risks.push('Low credit score'); }
    else { risks.push('Very low credit score — high default risk'); }

    const emi = amt / (tenure || 1);
    const dtiRatio = totalIncome > 0 ? emi / totalIncome : 999;
    if (dtiRatio < 0.3) { score += 25; flags.push({ text: `EMI/Income ratio ${(dtiRatio * 100).toFixed(0)}% — excellent`, type: 'good' }); }
    else if (dtiRatio < 0.5) { score += 15; flags.push({ text: `EMI/Income ratio ${(dtiRatio * 100).toFixed(0)}% — acceptable`, type: 'ok' }); }
    else { risks.push(`EMI/Income ratio ${(dtiRatio * 100).toFixed(0)}% — too high`); }

    if (form.employment === 'Unemployed') { risks.push('Unemployed applicant — auto-flag'); score -= 20; }
    else if (form.employment === 'Salaried') { score += 15; flags.push({ text: 'Stable salaried income', type: 'good' }); }
    else if (form.employment === 'Self-Employed' && bizYears >= 3) { score += 10; flags.push({ text: `${bizYears} years in business`, type: 'ok' }); }
    else if (form.employment === 'Self-Employed' && bizYears < 3) { risks.push('Business < 3 years — limited track record'); }

    if (form.loanType === 'Home Loan' || form.loanType === 'Auto Loan') {
      if (collateralVal >= amt * 0.8) { score += 20; flags.push({ text: 'Collateral covers 80%+ of loan', type: 'good' }); }
      else if (collateralVal > 0) { score += 10; flags.push({ text: 'Collateral partially covers loan', type: 'ok' }); }
      else { risks.push(`${form.loanType} typically requires collateral`); }
    }

    if (form.coApplicant && coIncome > 0) { score += 10; flags.push({ text: 'Co-applicant income strengthens application', type: 'good' }); }

    const maxEligible = totalIncome * tenure * 0.4;

    let decision, color;
    if (score >= 60 && risks.length <= 1) { decision = 'APPROVED'; color = '#2e7d32'; }
    else if (score >= 40) { decision = 'CONDITIONAL APPROVAL'; color = '#e65100'; }
    else { decision = 'DECLINED'; color = '#c62828'; }

    setResult({ score, decision, color, flags, risks, maxEligible, dtiRatio, emi });
  };

  const needsCollateral = form.loanType === 'Home Loan' || form.loanType === 'Auto Loan';
  const needsBizYears = form.employment === 'Self-Employed' || form.employment === 'Freelancer';

  return (
    <div className="eval-section">
      <h2 className="eval-section-title">🧮 Loan Eligibility Engine <span className="eval-badge-scenario">Conditional Logic / Reasoning</span></h2>
      <p className="eval-desc">Test: dynamic field visibility based on selections, multi-factor scoring logic, reasoned decision output.</p>

      <div className="cw-grid">
        <div className="cw-form">
          <div className="cw-row">
            <label>Loan Type</label>
            <select name="loanType" value={form.loanType} onChange={ch}>
              {LOAN_TYPES.map(l => <option key={l}>{l}</option>)}
            </select>
          </div>
          <div className="cw-row">
            <label>Loan Amount (₹)</label>
            <input name="amount" value={form.amount} onChange={ch} placeholder="e.g. 500000" type="number" />
          </div>
          <div className="cw-row">
            <label>Tenure (months)</label>
            <input name="tenure" value={form.tenure} onChange={ch} placeholder="e.g. 36" type="number" />
          </div>
          <div className="cw-row">
            <label>Employment Type</label>
            <select name="employment" value={form.employment} onChange={ch}>
              {EMPLOYMENT.map(e => <option key={e}>{e}</option>)}
            </select>
          </div>
          {needsBizYears && (
            <div className="cw-row cw-conditional">
              <label>Years in Business</label>
              <input name="businessYears" value={form.businessYears} onChange={ch} placeholder="e.g. 5" type="number" />
              <span className="cw-hint">Shown because employment is {form.employment}</span>
            </div>
          )}
          <div className="cw-row">
            <label>Monthly Income (₹)</label>
            <input name="monthlyIncome" value={form.monthlyIncome} onChange={ch} placeholder="e.g. 50000" type="number" />
          </div>
          <div className="cw-row">
            <label>Credit Score</label>
            <input name="creditScore" value={form.creditScore} onChange={ch} placeholder="300–900" type="number" min="300" max="900" />
          </div>

          <div className="cw-row">
            <label className="cw-chk-lbl"><input type="checkbox" name="coApplicant" checked={form.coApplicant} onChange={ch} /> Add Co-Applicant</label>
          </div>
          {form.coApplicant && (
            <div className="cw-row cw-conditional">
              <label>Co-Applicant Monthly Income (₹)</label>
              <input name="coApplicantIncome" value={form.coApplicantIncome} onChange={ch} placeholder="e.g. 30000" type="number" />
              <span className="cw-hint">Combined income improves eligibility</span>
            </div>
          )}

          {needsCollateral && (
            <>
              <div className="cw-row cw-conditional">
                <label className="cw-chk-lbl"><input type="checkbox" name="collateral" checked={form.collateral} onChange={ch} /> Provide Collateral</label>
                <span className="cw-hint">Required for {form.loanType}</span>
              </div>
              {form.collateral && (
                <div className="cw-row cw-conditional">
                  <label>Collateral Value (₹)</label>
                  <input name="collateralValue" value={form.collateralValue} onChange={ch} placeholder="e.g. 400000" type="number" />
                </div>
              )}
            </>
          )}

          <button className="eval-btn eval-btn-primary cw-analyze-btn" onClick={analyze}>Analyze Eligibility</button>
        </div>

        {result && (
          <div className="cw-result">
            <div className="cw-decision" style={{ borderColor: result.color, color: result.color }}>
              {result.decision}
            </div>
            <div className="cw-score-bar-wrap">
              <div className="cw-score-label">Eligibility Score: <strong>{result.score}</strong>/100</div>
              <div className="cw-score-bar">
                <div className="cw-score-fill" style={{ width: `${Math.max(0, Math.min(100, result.score))}%`, background: result.color }} />
              </div>
            </div>
            <div className="cw-metrics">
              <div><span>Monthly EMI:</span><strong>₹{Math.round(result.emi).toLocaleString()}</strong></div>
              <div><span>DTI Ratio:</span><strong>{(result.dtiRatio * 100).toFixed(1)}%</strong></div>
              <div><span>Max Eligible:</span><strong>₹{Math.round(result.maxEligible).toLocaleString()}</strong></div>
            </div>
            {result.flags.length > 0 && (
              <div className="cw-flags">
                <div className="cw-flags-title">Positive Factors</div>
                {result.flags.map((f, i) => (
                  <div key={i} className={`cw-flag cw-flag-${f.type}`}>✓ {f.text}</div>
                ))}
              </div>
            )}
            {result.risks.length > 0 && (
              <div className="cw-flags">
                <div className="cw-flags-title">Risk Factors</div>
                {result.risks.map((r, i) => (
                  <div key={i} className="cw-flag cw-flag-risk">⚠ {r}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
